import Konva from 'konva';
import { useCallback, useEffect, useRef } from 'react';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationProps, AnimationType } from '../types';
import { clampToHex, parseColor } from '../utils/canvas';

export const useAnimation = () => {
  const previewMode = useCanvasStore((s) => s.previewMode);
  const canvasImages = useCanvasStore((s) => s.canvasImages);
  const stageRef = useCanvasStore((s) => s.stageRef);

  const prevPreviewModeRef = useRef(false);

  const createAnimation = useCallback(
    (node: Konva.Node, animationType: AnimationType, props: AnimationProps): Konva.Animation | null => {
      if (!animationType || !node) return null;

      const layer = node.getLayer();
      if (!layer) return null;

      let animation: Konva.Animation | null = null;
      const duration = props.duration ? Math.min(Math.max(Number(props.duration), 100), 10000) : 1000;

      if (props.originalX === undefined) props.originalX = node.x();
      if (props.originalY === undefined) props.originalY = node.y();
      const originalX = props.originalX;
      const originalY = props.originalY;

      switch (animationType) {
        case 'fade-in':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const opacity = 0.4 + 0.6 * Math.abs(Math.sin(frame.time / 2000));
            node.opacity(opacity);
          }, layer);
          break;

        case 'slide-up': {
          const initialY = node.y();
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const offset = 10 * Math.sin(frame.time / 1500);
            node.y(initialY - offset);
          }, layer);
          break;
        }

        case 'zoom-in':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const scale = 0.9 + 0.2 * Math.abs(Math.sin(frame.time / 1250));
            node.scale({ x: scale, y: scale });
          }, layer);
          break;

        case 'simple-fade':
          node.opacity(0);
          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            node.opacity(progress);
            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;

        case 'slide-from-left': {
          const nodeWidth = node.width();
          node.x(-nodeWidth);

          const numericOriginalX = typeof originalX === 'number' ? originalX : Number(originalX);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;

            node.x(-nodeWidth + (numericOriginalX + nodeWidth) * ease);

            if (progress >= 1 && animation) {
              node.x(numericOriginalX);
              animation.stop();
            }
          }, layer);
          break;
        }

        case 'slide-from-right': {
          node.x(CANVAS_WIDTH);

          const numericOriginalX = typeof originalX === 'number' ? originalX : Number(originalX);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;

            node.x(CANVAS_WIDTH - (CANVAS_WIDTH - numericOriginalX) * ease);

            if (progress >= 1 && animation) {
              node.x(numericOriginalX);
              animation.stop();
            }
          }, layer);
          break;
        }

        case 'slide-from-bottom': {
          node.y(CANVAS_HEIGHT);

          const numericOriginalY = typeof originalY === 'number' ? originalY : Number(originalY);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;

            node.y(CANVAS_HEIGHT - (CANVAS_HEIGHT - numericOriginalY) * ease);

            if (progress >= 1 && animation) {
              node.y(numericOriginalY);
              animation.stop();
            }
          }, layer);
          break;
        }

        case 'rotate':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const angle = ((frame.time % 6000) / 6000) * 360;
            node.rotation(angle);
          }, layer);
          break;

        case 'bounce': {
          const bounceY = node.y();
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const time = frame.time / 1000;
            const amplitude = 15 * (0.8 + 0.2 * Math.sin(time / 2));
            const bounce = -Math.abs(Math.sin(time * 3)) * amplitude;
            node.y(bounceY + bounce);
          }, layer);
          break;
        }

        case 'color-pulse':
          if (props.color && node instanceof Konva.Text) {
            const originalColor = parseColor(props.color);

            animation = new Konva.Animation((frame) => {
              if (!frame) return;
              const time = frame.time / 3000;
              const r = originalColor.r + Math.sin(time) * 40;
              const g = originalColor.g + Math.sin(time + 2) * 40;
              const b = originalColor.b + Math.sin(time + 4) * 40;

              const hexColor = `#${clampToHex(r)}${clampToHex(g)}${clampToHex(b)}`;
              node.fill(hexColor);
            }, layer);
          }
          break;

        case 'move': {
          node.opacity(0);
          const numericOriginalX = typeof originalX === 'number' ? originalX : Number(originalX);
          node.x(numericOriginalX - 50);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 2;
            node.x(numericOriginalX - 50 * (1 - ease));
            node.opacity(ease);

            if (progress >= 1 && animation) {
              node.x(numericOriginalX);
              node.opacity(1);
              animation.stop();
            }
          }, layer);
          break;
        }

        case 'flip-horizontal': {
          const originalScaleX = node.scaleX();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const progress = (frame.time % duration) / duration;

            const scale = originalScaleX * Math.cos(Math.PI * progress);
            node.scaleX(scale);

            if (progress > 0.9) {
              const bounce = Math.sin((progress - 0.9) * 10 * Math.PI) * 0.1;
              node.scaleX(scale * (1 + bounce));
            }
          }, layer);
          break;
        }

        case 'flip-vertical': {
          const originalScaleY = node.scaleY();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const progress = (frame.time % duration) / duration;

            const scale = originalScaleY * Math.cos(Math.PI * progress);
            node.scaleY(scale);

            if (progress > 0.9) {
              const bounce = Math.sin((progress - 0.9) * 10 * Math.PI) * 0.1;
              node.scaleY(scale * (1 + bounce));
            }
          }, layer);
          break;
        }

        case 'pulse':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const scale = 1 + 0.1 * Math.sin(frame.time / (duration / 2));
            node.scale({ x: scale, y: scale });
          }, layer);
          break;

        case 'spin':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const angle = ((frame.time % (duration * 2)) / (duration * 2)) * 360;
            node.rotation(angle);
          }, layer);
          break;

        case 'swing':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const angle = 20 * Math.sin((frame.time / duration) * Math.PI);
            node.rotation(angle);
          }, layer);
          break;

        case 'float': {
          const floatY = node.y();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const offset = 15 * Math.sin(frame.time / (duration * 1.5));
            node.y(floatY + offset);
          }, layer);
          break;
        }

        case 'wiggle': {
          const wiggleX = node.x();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const offset = 8 * Math.sin(frame.time / (duration / 4));
            node.x(wiggleX + offset);
          }, layer);
          break;
        }

        case 'blur-in': {
          node.opacity(0);
          node.scale({ x: 1.2, y: 1.2 });

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 2;

            node.opacity(ease);
            node.scale({
              x: 1.2 - 0.2 * ease,
              y: 1.2 - 0.2 * ease,
            });

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'grow': {
          node.scale({ x: 0.3, y: 0.3 });
          node.opacity(0.5);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 2;

            node.scale({
              x: 0.3 + 0.7 * ease,
              y: 0.3 + 0.7 * ease,
            });
            node.opacity(0.5 + 0.5 * ease);

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'shrink': {
          node.scale({ x: 1.2, y: 1.2 });

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);

            node.scale({
              x: 1.2 - 0.2 * progress,
              y: 1.2 - 0.2 * progress,
            });

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'slide-from-top': {
          node.y(-node.height());

          const numericOriginalY = typeof originalY === 'number' ? originalY : Number(originalY);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;

            node.y(-node.height() + (numericOriginalY + node.height()) * ease);

            if (progress >= 1 && animation) {
              node.y(numericOriginalY);
              animation.stop();
            }
          }, layer);
          break;
        }

        case 'rubber-band': {
          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);

            let scaleX = 1;
            let scaleY = 1;

            if (progress < 0.3) {
              scaleX = 1 + progress * 2;
              scaleY = 1 - progress * 0.5;
            } else if (progress < 0.6) {
              scaleX = 1.6 - (progress - 0.3) * 2;
              scaleY = 0.85 + (progress - 0.3) * 0.5;
            } else if (progress < 0.8) {
              scaleX = 1 + (progress - 0.6) * 0.6;
              scaleY = 1 - (progress - 0.6) * 0.3;
            } else {
              scaleX = 1.12 - (progress - 0.8) * 0.6;
              scaleY = 0.94 + (progress - 0.8) * 0.3;
            }

            node.scale({ x: scaleX, y: scaleY });

            if (progress >= 1 && animation) {
              node.scale({ x: 1, y: 1 });
              animation.stop();
            }
          }, layer);
          break;
        }

        default:
          return null;
      }

      animation?.start();
      return animation;
    },
    []
  );

  useEffect(() => {
    if (!previewMode) return;

    const animations: Konva.Animation[] = [];
    const initialStates = new Map();
    const delayTimers: NodeJS.Timeout[] = [];
    const delayedNodes = new Set();

    const texts = useCanvasStore.getState().texts;
    const animatedItems = [
      ...canvasImages.map((img) => ({ ...img, type: 'image' })),
      ...texts.filter((t) => t.animation && t.animation !== 'none').map((t) => ({ ...t, type: 'text' })),
    ].filter((item) => {
      if (item.type === 'image') {
        return item.animation && item.animation !== 'none' && !(item as typeof item & { deleted?: boolean }).deleted;
      }
      return item.animation && item.animation !== 'none';
    });

    const nodesOriginalPositions = new Map();

    animatedItems.forEach((item) => {
      const nodeId = item.type === 'text' ? `text-${item.id}` : item.id;
      const node = stageRef.current?.findOne(`#${nodeId}`);
      if (!node) return;

      const originalState = {
        x: node.x(),
        y: node.y(),
        width: node.width?.() || 0,
        height: node.height?.() || 0,
        opacity: node.opacity() || 1,
        scale: { x: node.scaleX() || 1, y: node.scaleY() || 1 },
        rotation: node.rotation() || 0,
        visible: node.visible(),
      };

      nodesOriginalPositions.set(item.id, originalState);
      initialStates.set(item.id, originalState);
    });

    animatedItems.forEach((item) => {
      const nodeId = item.type === 'text' ? `text-${item.id}` : item.id;
      const node = stageRef.current?.findOne(`#${nodeId}`);
      if (!node) return;

      const originalPosition = nodesOriginalPositions.get(item.id);
      if (!originalPosition) return;

      const delay = Math.min(5000, Math.max(0, Number(item.animationDelay || 0) * 1000));
      const duration = Math.min(10000, Math.max(100, Number(item.animationDuration || 1) * 1000));

      const animationType = item.animation || 'none';

      if (delay > 0) {
        node.visible(false);
        node.opacity(0);
        delayedNodes.add(item.id);

        const layer = node.getLayer();
        if (layer) layer.batchDraw();
      } else {
        if (animationType !== 'none') {
          setInitialAnimationPosition(node, animationType, originalPosition);
        }
      }

      const delayTimer = setTimeout(() => {
        delayedNodes.delete(item.id);
        node.visible(true);

        if (delay > 0 && animationType !== 'none') {
          setInitialAnimationPosition(node, animationType, originalPosition);

          const layer = node.getLayer();
          if (layer) layer.batchDraw();

          setTimeout(() => {
            const anim = createAnimation(node, animationType, {
              duration,
              originalX: originalPosition.x,
              originalY: originalPosition.y,
              ...item,
            });

            if (anim) animations.push(anim);
          }, 50);
        } else if (animationType !== 'none') {
          const anim = createAnimation(node, animationType, {
            duration,
            originalX: originalPosition.x,
            originalY: originalPosition.y,
            ...item,
          });

          if (anim) animations.push(anim);
        }
      }, delay);

      delayTimers.push(delayTimer);
    });

    function setInitialAnimationPosition(
      node: Konva.Node,
      animation: AnimationType,
      originalPos: Record<string, unknown>
    ) {
      if (animation === 'slide-from-bottom') {
        node.y(CANVAS_HEIGHT);
      } else if (animation === 'slide-from-top') {
        node.y(-node.height());
      } else if (animation === 'slide-from-left') {
        node.x(-node.width());
      } else if (animation === 'slide-from-right') {
        node.x(CANVAS_WIDTH);
      } else if (animation === 'zoom-in') {
        node.scaleX(0.8);
        node.scaleY(0.8);
      } else if (animation === 'simple-fade') {
        node.opacity(0);
      }
    }

    return () => {
      delayTimers.forEach((timer) => clearTimeout(timer));
      animations.forEach((anim) => anim.stop());

      delayedNodes.forEach((id) => {
        const isText = texts.some((t) => t.id === id);
        const nodeId = isText ? `text-${id}` : id;
        const node = stageRef.current?.findOne(`#${nodeId}`);

        if (node) {
          node.visible(true);
          node.opacity(1);
        }
      });

      initialStates.forEach((state, id) => {
        const isText = texts.some((t) => t.id === id);
        const nodeId = isText ? `text-${id}` : id;
        const node = stageRef.current?.findOne(`#${nodeId}`);

        if (node) {
          node.x(state.x);
          node.y(state.y);
          node.visible(true);
          node.opacity(state.opacity || 1);

          if (state.scale) {
            node.scaleX(state.scale.x);
            node.scaleY(state.scale.y);
          }
          if (state.rotation !== undefined) {
            node.rotation(state.rotation);
          }
        }
      });

      if (stageRef.current) {
        const allNodes = stageRef.current.find('Image, Text');
        allNodes.forEach((node) => {
          if (!node.visible()) {
            node.visible(true);
            node.opacity(1);
          }
        });

        stageRef.current.getLayers().forEach((layer) => layer.batchDraw());
      }
    };
  }, [previewMode, canvasImages, stageRef, createAnimation]);

  useEffect(() => {
    if (prevPreviewModeRef.current === true && previewMode === false) {
      setTimeout(() => {
        if (!stageRef.current) return;

        const allNodes = stageRef.current.find('Image, Text');

        allNodes.forEach((node) => {
          node.visible(true);
          node.opacity(1);

          if (node.getClassName() === 'Image') {
            const imageId = node.id();
            const imageData = canvasImages.find((img) => img.id === imageId);
            if (imageData) {
              node.x(imageData.x);
              node.y(imageData.y);
              node.scaleX(1);
              node.scaleY(1);
              if (imageData.rotation) node.rotation(imageData.rotation);
            }
          } else if (node.getClassName() === 'Text') {
            const textId = node.id().replace('text-', '');
            const textData = useCanvasStore.getState().texts.find((t) => t.id === textId);
            if (textData) {
              node.x(textData.x);
              node.y(textData.y);
              node.scaleX(1);
              node.scaleY(1);
            }
          }
        });

        stageRef.current.getLayers().forEach((layer) => layer.batchDraw());
      }, 100);
    }

    prevPreviewModeRef.current = previewMode;
  }, [previewMode, canvasImages, stageRef]);

  return { createAnimation };
};

export default useAnimation;
